import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Badge,
  Button,
  ChevronLeftIcon,
  CircleArrowDownIcon as DownloadIcon,
  CloseIcon,
  OpenIcon as OpenExternallyIcon,
  PageMessage,
  toast,
} from "@picklebet/ui";
import { ReactComponent as PromosUnavailable } from "assets/illustrations/no-promo.svg";

import cx from "classnames";
import { useIsMobile } from "@/hooks/useDisplay";
import { useLocationParams } from "@/hooks/useLocationParams";
import { useDebounce } from "usehooks-ts";
import { useIsClient } from "@/hooks/useIsClient";
import { stringify } from "query-string";
import { navigate } from "utilities/navigate";
import { ReactComponent as SpeechBubble } from "../assets/chat-speech.svg";
import { CreateATicket } from "./CreateATicket";
import { useLocation } from "@/hooks/useLocation";
import { isWebView } from "utilities/display";
import { PICKLEBET_DOMAINS } from "appConfig";
import { getBridge } from "utilities/bridge";
import { useSignIn } from "@/features/auth/hooks/useSignIn";
import { openWithChat, open } from "utilities/support";
import {
  useZendeskDomain,
  useZendeskKey,
  useZendeskMessengerToken,
} from "@/hooks/firestore/user/useZendesk";
import { convertErrorToToast } from "utilities/errorsParser";
import appConfig from "appConfig";

import { formatUserDateTime } from "@/utilities/datetime";
import { HELP_CENTER_EXPORT_DATETIME } from "@/utilities/dateTimeFormats";
import { useHelpCenterStore } from "@/features/help-center/stores/helpCenterStore";
import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import * as styles from "./HelpCenter.module.scss";

const PROMOTIONAL_LABEL = "promotional";
const eventListenersMap = new WeakMap();

const cleanAndFormatHTML = (html: string) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const elements = doc.querySelectorAll("*");

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    element.removeAttribute("class");
    element.removeAttribute("style");
    element.removeAttribute("size");

    if (element.tagName === "A") {
      try {
        const elem = element.getAttribute("href");
        const domain = elem ? new URL(elem)?.hostname : "";
        if (domain !== "picklebet.com") {
          element.setAttribute("target", "_blank");
        }
      } catch (e) {
        console.warn(element.getAttribute("href"));
        console.warn(e);
        element.removeAttribute("href");
      }
    }
  }

  return doc.body.innerHTML;
};

const ZENDESK_ARTICLE_URL_PATH = "/hc/en-us/articles/";
const ZENDESK_ARTICLE_API_URL_PATH = "/api/v2/help_center/en-us/articles/";
const ZENDESK_ARTICLE_API_URL_SUFFIX = ".json";

const replacePicklebetDomain = (url: string) =>
  PICKLEBET_DOMAINS.reduce((acc, domain) => acc.replace(domain, ""), url);

export type ZendeskSearchResultItem = {
  author_id: number;
  body: string;
  created_at: string;
  draft: boolean;
  html_url: string;
  id: number;
  label_names: string[];
  promoted: boolean;
  position: number;
  source_locale: string;
  title: string;
  updated_at: string;
  url: string;
  vote_sum: number;
  vote_count: number;
  rank: number;
};

export const HelpCenter = () => {
  const isClient = useIsClient();
  const isMobile = useIsMobile();
  const [results, setResults] = useState<ZendeskSearchResultItem[]>([]);
  const [createATicketOpen, setCreateATicketOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [selectedResult, setSelectedResult] =
    useState<ZendeskSearchResultItem | null>();
  const livechat = useLocationParams("livechat");
  const zendeskLabels = useLocationParams("zendesk-labels");
  const location = useLocation();
  const zendeskMessengerToken = useZendeskMessengerToken();
  const zendeskDomain = useZendeskDomain();
  const zendeskKey = useZendeskKey();
  const { handleSignIn, isBiometricProcessing } = useSignIn();
  const unreadCount = useHelpCenterStore((s) => s.unreadCount);
  const zendeskHasLoaded = useHelpCenterStore((s) => s.zendeskHasLoaded);
  const chatOpen = useHelpCenterStore((s) => s.chatOpen);
  const chatRequested = useHelpCenterStore((s) => s.chatRequested);
  const labels = useHelpCenterStore((s) => s.labels);
  const search = useHelpCenterStore((s) => s.search);
  const visible = useHelpCenterStore((s) => s.visible);
  const articleId = useHelpCenterStore((s) => s.articleId);
  const debouncedSearch = useDebounce(search, 500);

  const zendeskHelpDomains = useMemo(() => {
    const domains = [
      "picklebettest.zendesk.com",
      "help.picklebet.com",
      "picklebet-international.zendesk.com",
      "picklebet-international1745290020.zendesk.com",
      "picklebet1745289952.zendesk.com",
    ];
    if (!zendeskDomain || typeof zendeskDomain === "undefined") {
      return domains;
    }
    return [...domains, zendeskDomain];
  }, [zendeskDomain]);

  const zendeskSearchUrl = useMemo(
    () =>
      `https://${zendeskDomain}/api/v2/help_center/articles/embeddable_search.json`,
    [zendeskDomain],
  );

  const zendeskArticleApiUrl = useMemo(
    () => `https://${zendeskDomain}${ZENDESK_ARTICLE_API_URL_PATH}`,
    [zendeskDomain],
  );

  const [lastDomainAndKey, setLastDomainAndKey] = useState<string>("");
  const isDragging = useRef(false);

  useEffect(() => {
    // reset if domain or key change
    const currentDomainAndKey = `${zendeskDomain}-${zendeskKey}`;
    if (lastDomainAndKey === currentDomainAndKey) {
      return;
    }
    // keep track of the new one
    setLastDomainAndKey(currentDomainAndKey);

    // key or domain changed - reset selections in case user doesn't have access to what's been chosen
    setResults([]);
    setSelectedResult(null);
  }, [zendeskDomain, zendeskKey]);

  /**
   * Remove single param from url
   * @param param
   */
  const removeGetParamFromUrl = (param: string) => {
    const url = new URL(`${location.href}`);
    url.searchParams.delete(param);
    navigate(decodeURI(`${url.pathname}${url.search}`), false, {
      replace: true,
    });
  };

  useEffect(() => {
    if (zendeskHasLoaded && livechat === "true") {
      if (!chatOpen) {
        openWithChat();
      }
      removeGetParamFromUrl("livechat");
    }
  }, [zendeskHasLoaded, livechat]);

  useEffect(() => {
    if (!zendeskHasLoaded || !chatRequested) {
      return;
    }

    useHelpCenterStore.getState().clearChatRequest();

    if (chatOpen) {
      return;
    }

    openWithChat();
  }, [zendeskHasLoaded, chatRequested]);

  useEffect(() => {
    if (zendeskHasLoaded && zendeskLabels) {
      if (typeof zendeskLabels === "string") {
        open({ labels: zendeskLabels.split(",") }, false);
      }
      removeGetParamFromUrl("zendesk-labels");
    }
  }, [zendeskHasLoaded, zendeskLabels]);

  // in case we fail to intercept the click and the browser opens a new tab
  useEffect(() => {
    if (zendeskHasLoaded && location.search.includes("submit-a-request")) {
      navigate("/", false, { replace: true });
      setCreateATicketOpen(true);
      useHelpCenterStore.getState().openHelpCenter();
    }
  }, [zendeskHasLoaded]);

  useEffect(() => {
    setLoading(true);
  }, [search]);

  const addEventListenerOnce = (
    element: Document,
    eventType: string,
    handler: (e: any) => boolean,
  ) => {
    let listeners = eventListenersMap.get(element);

    if (!listeners) {
      listeners = {};
      eventListenersMap.set(element, listeners);
    }

    if (listeners[eventType]) {
      return;
    }

    element.addEventListener(eventType, handler);
    listeners[eventType] = handler;
  };

  // attach handleClick specifically to the iframe - zendesk chat widget
  const attachToChatClickEvents = () => {
    const messagingWindow = window.document.querySelector(
      "[title='Messaging window']",
    ) as HTMLIFrameElement;

    if (messagingWindow && messagingWindow.contentDocument) {
      addEventListenerOnce(
        messagingWindow.contentDocument,
        "click",
        handleClick,
      );
    } else {
      setTimeout(() => {
        attachToChatClickEvents();
      }, 500);
    }
  };

  useEffect(() => {
    if (!zendeskHasLoaded) return;

    if (chatOpen) {
      attachToChatClickEvents();
    }
  }, [zendeskMessengerToken, chatOpen]);

  const filteredResults = useMemo(() => {
    if (zendeskMessengerToken) return results;
    return results.filter((result) => {
      const resultLabels = result.label_names;
      return resultLabels.every((label) => PROMOTIONAL_LABEL !== label);
    });
  }, [results, zendeskMessengerToken]);

  const doSearch = async () => {
    try {
      if (search !== "" || labels.length > 0) {
        const query = {
          query: search !== "" ? search : undefined,
          locale: "en-us",
          per_page: "20",
          origin: "web_widget",
          label_names: labels.length > 0 ? labels : undefined,
        };

        if (search === "") {
          delete query.query;
        }

        if (labels.length === 0) {
          delete query.label_names;
        }

        const response = await fetch(
          `${zendeskSearchUrl}?${stringify(query, { arrayFormat: "comma" })}`,
          {
            method: "GET",
          },
        );

        const body = await response.json();

        if (body.results.length > 0) {
          // just choose the first one if we have a single result, and we aren't using the search
          if (body.results.length === 1 && search === "") {
            setResults([]);
            setSelectedResult(body.results[0]);
          } else {
            // or list all results
            setResults(body.results);
            setSelectedResult(null);
          }
        } else {
          setResults([]);
          setSelectedResult(null);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getHelpCenterArticleHtml = async (url: string) => {
    setLoading(true);
    try {
      const response = await fetch(url, {
        method: "GET",
      });

      const body = await response.json();

      if (body.article) {
        setSelectedResult(body.article);
        useHelpCenterStore.getState().openHelpCenter();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openLinkExternallyOfTheApps = (href: string) => {
    getBridge().send.openExternally({ url: href });
  };

  const handleClick = useCallback(
    (e: MouseEvent & any) => {
      if (isDragging.current) {
        isDragging.current = false;
        return false;
      }

      const origin =
        e.target?.closest(`a`) ||
        e.target.closest("button") ||
        e.target.closest("button")?.querySelector("[data-helpcenter]") ||
        e.target.closest("div")?.firstElementChild; // Click fires on Carousel.Root but we need Carousel.Item

      const href =
        origin?.getAttribute("href") ?? origin?.getAttribute("data-helpcenter");

      if (!origin || !href) {
        return true;
      }

      const openBlank = origin?.getAttribute("target") === "_blank";

      if (href.includes(".pdf")) {
        // Short circuit for .pdfs in Android.tsx and iOS.tsx (Apps).
        if (isWebView()) {
          openLinkExternallyOfTheApps(href);
          return false;
        }

        return false;
      } else if (href.includes("help-center")) {
        useHelpCenterStore.getState().openHelpCenter();
        e.preventDefault();
        e.stopPropagation();

        return false;
      } else if (href.includes("live-chat")) {
        useHelpCenterStore.getState().openHelpCenterChat();
        e.preventDefault();
        e.stopPropagation();

        return false;
      } else if (href.includes("submit-a-request")) {
        setCreateATicketOpen(true);
        useHelpCenterStore.getState().closeHelpCenterChat();
        useHelpCenterStore.getState().openHelpCenter();
        e.preventDefault();
        e.stopPropagation();

        return false;
      } else if (
        PICKLEBET_DOMAINS.some((domain) => href.includes(domain)) &&
        !openBlank
      ) {
        e.preventDefault();
        e.stopPropagation();
        navigate(replacePicklebetDomain(href));
        if (isMobile) {
          useHelpCenterStore.getState().closeHelpCenterChat();
          useHelpCenterStore.getState().closeHelpCenter();
        }
        return false;
      } else if (
        zendeskHelpDomains.some((domain) => href.includes(domain)) &&
        // Allows specific links to opt out of the widget and open the article
        // in a new tab instead (e.g. ?overrideHelpCenter=true on the href).
        !href.includes("overrideHelpCenter=true")
      ) {
        e.preventDefault();
        e.stopPropagation();
        // it's one of our zendesk links, so we want to open it in the widget
        // replace "/hc/en-us/articles/" with "/api/v2/help_center/en-us/articles/" and add ".json" on the end to get the content
        getHelpCenterArticleHtml(
          `${href.replace(
            ZENDESK_ARTICLE_URL_PATH,
            ZENDESK_ARTICLE_API_URL_PATH,
          )}${ZENDESK_ARTICLE_API_URL_SUFFIX}`,
        );
        useHelpCenterStore.getState().closeHelpCenterChat();
        useHelpCenterStore.getState().openHelpCenter();
        return false;
      }
      return true;
    },
    [navigate, getHelpCenterArticleHtml],
  );

  useEffect(() => {
    if (!zendeskHasLoaded) return;
    const handleMouseDown = () => {
      isDragging.current = false;
    };

    const handleMouseMove = () => {
      isDragging.current = true;
    };

    window.document.addEventListener("click", handleClick);
    window.document.addEventListener("mousedown", handleMouseDown);
    window.document.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.document.removeEventListener("click", handleClick);
      window.document.removeEventListener("mousedown", handleMouseDown);
      window.document.removeEventListener("mousemove", handleMouseMove);
    };
  }, [zendeskHasLoaded, chatOpen, selectedResult, visible, location]);

  useEffect(() => {
    doSearch();
  }, [debouncedSearch]);

  useEffect(() => {
    if (!zendeskHasLoaded || !window.zE) return;
    window.zE("messenger:on", "close", () => {
      useHelpCenterStore.getState().closeHelpCenterChat();
    });

    window.zE("messenger:on", "unreadMessages", (count: number) => {
      useHelpCenterStore.getState().setUnreadCount(count);
    });
  }, [zendeskHasLoaded]);

  useEffect(() => {
    if (!zendeskHasLoaded || !window.zE) return;
    try {
      if (zendeskMessengerToken) {
        window.zE("messenger", "loginUser", (callback: (s: string) => void) => {
          callback(zendeskMessengerToken);
        });
      } else if (!zendeskMessengerToken) {
        window.zE("messenger", "logoutUser");
      }
    } catch (e) {
      console.error(e);
    }
  }, [zendeskMessengerToken, zendeskHasLoaded]);

  useEffect(() => {
    if (!zendeskHasLoaded || !window.zE) return;
    if (chatOpen) {
      window.zE("messenger", "open");
    }
    if (!chatOpen) {
      window.zE("messenger", "close");
    }
  }, [chatOpen, zendeskMessengerToken, zendeskHasLoaded]);

  useEffect(() => {
    if (labels.length > 0) {
      doSearch();
    }
  }, [labels]);

  useEffect(() => {
    if (articleId) {
      getHelpCenterArticleHtml(
        `${zendeskArticleApiUrl}${articleId}${ZENDESK_ARTICLE_API_URL_SUFFIX}`,
      );
    }
  }, [articleId]);

  const isLoggedIn = useIsLoggedIn();
  const wasDismissedForLogin = useHelpCenterStore(
    (s) => s.wasDismissedForLogin,
  );

  useEffect(() => {
    if (isLoggedIn && wasDismissedForLogin) {
      useHelpCenterStore.getState().setVisible(true);
      useHelpCenterStore.getState().setWasDismissedForLogin(false);
    }
  }, [isLoggedIn, wasDismissedForLogin]);

  const showPromotionalArticle =
    zendeskMessengerToken ||
    !selectedResult?.label_names?.includes(PROMOTIONAL_LABEL);

  const handleSearch = (e: any) => {
    useHelpCenterStore.getState().setHelpCenterSearch(e.target.value);
  };

  const downloadPdf = async (url: string, filename: string) => {
    setDownloadLoading(true);

    try {
      // note: use this if youre using the emulator
      // "http://localhost:5001/skrilla-t1/australia-southeast1/generatePdfFromUrl",
      const fetchResult = await fetch(
        `${appConfig.DOMAIN}/generatePdfFromUrl`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            url,
            filename,
            date: formatUserDateTime(new Date(), HELP_CENTER_EXPORT_DATETIME),
          }),
        },
      );

      if (!fetchResult.ok) {
        setDownloadLoading(false);
        toast(
          convertErrorToToast(fetchResult?.statusText, {
            title: "Unable to download PDF",
          }),
        );
        return;
      }

      const blob = await fetchResult.blob();

      // check if we are in the apps
      if (isWebView()) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Data = reader.result as string;
          getBridge().send.download({
            filename,
            data: base64Data,
          });
        };
        reader.readAsDataURL(blob);
        setDownloadLoading(false);
        return;
      }

      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(blobUrl);

      toast({
        description: "Download complete",
        variant: "success",
      });
      setDownloadLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  if (!zendeskDomain || !isClient) {
    return null;
  }

  return (
    <>
      {createATicketOpen ? (
        <CreateATicket
          visible={createATicketOpen}
          setVisible={setCreateATicketOpen}
        />
      ) : (
        <div className={cx(styles.helpCenter, { [styles.visible]: visible })}>
          {loading && <span className={styles.loader}></span>}
          <div className={styles.header}>
            {selectedResult && (
              <div className={styles.title}>
                <div
                  className={styles.titleLeft}
                  onClick={() => setSelectedResult(null)}
                >
                  <div className={styles.backIcon}>
                    <ChevronLeftIcon />
                  </div>
                  <div className={cx(styles.titleText, styles.back)}>Back</div>
                </div>
                <div className={styles.titleRight}>
                  {selectedResult.html_url && (
                    <>
                      {selectedResult.label_names.indexOf(
                        "PDF_downloadable",
                      ) !== -1 && (
                        <div
                          className={cx(styles.icon, styles.downloadIcon, {
                            [styles.downloadLoading]: downloadLoading,
                          })}
                          onClick={() => {
                            if (downloadLoading) {
                              return;
                            }
                            downloadPdf(
                              selectedResult.html_url,
                              `${selectedResult?.id || "article"}.pdf`,
                            );
                          }}
                        >
                          <DownloadIcon />
                        </div>
                      )}
                      <div
                        className={cx(styles.icon, styles.openInNewIcon)}
                        onClick={() => {
                          if (typeof window === "undefined") return;

                          try {
                            window.open(selectedResult.html_url, "_blank");
                          } catch (e: any) {
                            console.error(
                              "Couldn't open article:",
                              e.message ?? "An unexpected error occurred",
                            );
                          }
                        }}
                      >
                        <OpenExternallyIcon />
                      </div>
                    </>
                  )}
                  <div
                    className={styles.icon}
                    onClick={() =>
                      useHelpCenterStore.getState().closeHelpCenter()
                    }
                  >
                    <CloseIcon />
                  </div>
                </div>
              </div>
            )}
            {!selectedResult && (
              <>
                <div
                  className={styles.title}
                  onClick={() =>
                    useHelpCenterStore.getState().closeHelpCenter()
                  }
                >
                  <div className={styles.titleText}>Picklebet Help Center</div>
                  <div className={styles.icon}>
                    <CloseIcon />
                  </div>
                </div>
                <div className={styles.search}>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder="Search"
                    onChange={handleSearch}
                    value={search}
                  />
                </div>
              </>
            )}
          </div>
          {selectedResult && (
            <>
              <div className={styles.content}>
                <div className={styles.resultsTitle}>
                  {showPromotionalArticle ? selectedResult.title : ""}
                </div>
                {showPromotionalArticle ? (
                  <div
                    className={styles.result}
                    dangerouslySetInnerHTML={{
                      __html: cleanAndFormatHTML(selectedResult.body),
                    }}
                  ></div>
                ) : (
                  <PageMessage
                    image={<PromosUnavailable />}
                    title={
                      "Log in to see our latest members only Promotion offers."
                    }
                  >
                    <Button
                      size="sm"
                      loading={isBiometricProcessing}
                      onClick={() => {
                        handleSignIn();
                        useHelpCenterStore
                          .getState()
                          .setWasDismissedForLogin(true);
                        useHelpCenterStore.getState().setVisible(false);
                      }}
                    >
                      Log In
                    </Button>
                  </PageMessage>
                )}
              </div>
            </>
          )}

          {!selectedResult && (
            <div className={styles.content}>
              {filteredResults.length > 0 ? (
                <>
                  <div className={styles.resultsTitle}>Top Suggestions</div>
                  <div className={styles.results}>
                    {results.map((result) => (
                      <div
                        key={result.id}
                        className={styles.result}
                        onClick={() => getHelpCenterArticleHtml(result.url)}
                      >
                        <div>{result.title}</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div>
                  {search !== "" && !loading && (
                    <>
                      <div className={styles.resultsTitle}>No Results</div>
                      <div className={styles.result}>
                        We couldn't find any results for your search. <br /> Try
                        searching for something else.
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          <div
            className={styles.footer}
            onClick={() => useHelpCenterStore.getState().openHelpCenterChat()}
          >
            <div className={styles.liveChat}>Live Chat &nbsp; 💬 </div>
          </div>
        </div>
      )}
      {unreadCount > 0 && (
        <div
          className={styles.unreadMessages}
          onClick={() => useHelpCenterStore.getState().openHelpCenterChat()}
        >
          <SpeechBubble /> <Badge className={styles.badge}>{unreadCount}</Badge>
        </div>
      )}
    </>
  );
};
